import React from 'react';
import styled from 'styled-components';

import { Translate } from '@packages/i18n';
import { WishlistHelper, Product } from '@packages/common';
import { Text, Icon, themeGet, is } from '@packages/ui';
import { useABTestingConfig } from '@packages/shared';
import { TopRightElementBase } from './TopRightElementBase';

export const PRODUCT_TILE_HEART_SIZE = 16;

interface WishListButtonProps extends React.HTMLAttributes<HTMLButtonElement> {
  wishlistHelper: WishlistHelper;
  product: Product;
}

const WishListContainer = styled(TopRightElementBase)<{ centeredIcon: boolean }>`
  > span:first-child {
    margin-right: ${themeGet.space(1)};
    ${is('centeredIcon')`
      margin-left: ${themeGet.space(1)};
    `}
  }
`;

export const WishListButton: React.FC<WishListButtonProps> = ({ wishlistHelper, product, style }) => {
  const { NO_PRODUCT_TILE_WISHLIST_TEXT } = useABTestingConfig();
  const [isWishlisted, setIsWishlisted] = React.useState(wishlistHelper.isWishlisted(product));
  React.useEffect(() => {
    const sub = wishlistHelper.subscribe(() => {
      setIsWishlisted(wishlistHelper.isWishlisted(product));
    });
    return () => {
      sub.unsubscribe();
    };
  }, [product, wishlistHelper]);

  const handleWishlistToggle = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    const isWishlistedCurrent = !!isWishlisted; // use the value, not reference
    setIsWishlisted(!isWishlistedCurrent);
    wishlistHelper.toggle(product, isWishlistedCurrent);
  };
  return (
    <WishListContainer
      centeredIcon={NO_PRODUCT_TILE_WISHLIST_TEXT}
      style={style}
      onClick={handleWishlistToggle}
      data-testid="wishlist-toggle"
    >
      <Icon size={PRODUCT_TILE_HEART_SIZE} name="heartFilled" color={isWishlisted ? 'primary' : 'sandDune'} />
      {!NO_PRODUCT_TILE_WISHLIST_TEXT && (
        <Text tag="span" color={isWishlisted ? 'primary' : 'sandDune'}>
          {isWishlisted ? <Translate translation="wishlisted" /> : <Translate translation="notWishlisted" />}
        </Text>
      )}
    </WishListContainer>
  );
};

WishListButton.displayName = 'WishListButton';
